import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import addMinutes from 'date-fns/addMinutes';
import { load, ReCaptchaInstance } from 'recaptcha-v3';

import { WriterTestimonial } from '@blocks/types';
import type { PreAddedFiles } from '@blocks/types/files';
import { IWriter } from '@blocks/types/writers';

import { CLIENT_TYPE_BROWSER, DOMAIN_DEFAULT, USER_ROLE_CUSTOMER } from '../constants';
import { apipe, formatISOUtc, toSearchParams, toUrlEncoded } from '../utils';

import { handleApiErrorsSlugs } from './handleApiErrorsSlugs';

export const endpoints = {
  paper: '/widgets/dictionaries/paper_type',
  discipline: '/widgets/dictionaries/discipline_sub,discipline_sub_grouping',
  settings: '/common/settings',
  writer: '/widgets/writers',
  writerTestimonials: '/widgets/writers/{slug}/testimonials',
  writerPortfolioList: '/widgets/writers/{slug}/portfolio',
  writerPortfolioDetails: '/widgets/writers/{slug}/portfolio/{id}',
  offset: '/time/utc-offset',
  userCheck: '/users/pre-check',
  userSession: '/users/sessions',
  customer: '/customers',
  preOrder: '/customers/pre-orders',
  orderDetails: '/customers/orders/{id}',
  databox: '/widgets/data-boxes',
  testimonials: '/widgets/testimonials-rotator',
  latestOrders: '/widgets/orders-latest',
  dictionaries: '/widgets/dictionaries',
  topWriters: '/widgets/writers-top',
  passwordRestore: '/users/password-restore',
  googleOAuth2: '/users/sessions/oauth2/url/google',
  appleOAuth2: '/users/sessions/oauth2/url/apple',
  validateCode: '/users/password-restore/actions/validate',
  writerRegistration: '/widgets/writer-registration',
  becomeWriterRequest: '/widgets/writer-registration-request',
  statistics: '/widgets/statistics',
  reminders: '/widgets/reminders',
  postOrder: '/widgets/order-post-experience',
  preAddFile: '/widgets/orphanage-file',
};

/**
 * @param {String} [domain]
 * @param {String} key
 * @returns {String} endpoint
 */
export const api = (domain = DOMAIN_DEFAULT, key: keyof typeof endpoints) => {
  return `${domain !== DOMAIN_DEFAULT ? `https://${domain}` : ''}/api/en/1${endpoints[key]}`;
};

type PipeState = {
  domain: string;
  errors: Record<string, boolean | string>;
  recaptcha_site_key: string;
  recaptcha_action?: string;
  //eslint-disable-next-line  @typescript-eslint/no-explicit-any
  [key: string]: any;
};

const addApiError =
  (state: PipeState, custom = {}) =>
  (e: AxiosError) => {
    const { errors } = state || {};

    if (e.response) {
      const {
        data: {
          meta: {
            response: { message_slug },
          },
        },
      } = e.response;

      return { ...state, errors: { ...errors, [message_slug]: true, ...custom } };
    }
  };

/**
 * @TODO route (refactoring: redirectPoints hardcode)
 * @param {Object} state
 * @param {number} state.utc_offset_min
 * @param {authorization|short_order_form} state.source_context
 * @param {string} state.captcha_token
 * @param {boolean} state.policy_agree
 * @param {string} [state.url_path_redirect_success]
 * @param {string} [state.url_path_redirect_fail]
 * @param {boolean} [state.stay_signed_in]
 * @param {boolean} [state.marketing_agree]
 * @param {string} [state.client_type]
 */
export function getOAuth2Url(state: PipeState) {
  const {
    domain,
    provider,
    source_context,
    utc_offset_min,
    policy_agree,
    captcha_token,
    data_box_key,
    marketing_agree,
  } = state || {};

  const redirectPoints = {
    authorization: `web_dashboard_default?oaprovider=${provider}`,
    short_order_form: `/dashboard/customer/new-order?data_box_key=${data_box_key}&oaprovider=${provider}`,
  };

  const getRedirect = (source_context: keyof typeof redirectPoints) => {
    return redirectPoints[source_context] as
      | (typeof redirectPoints)['authorization']
      | (typeof redirectPoints)['short_order_form'];
  };

  return axios(api(domain, `${provider}OAuth2` as 'googleOAuth2' | 'appleOAuth2'), {
    params: {
      url_path_redirect_success: getRedirect(source_context),
      captcha_token,
      url_path_redirect_fail: '/',
      client_type: CLIENT_TYPE_BROWSER,
      source_context,
      utc_offset_min,
      policy_agree,
      marketing_agree,
    },
  }).then(({ data }) => ({ ...state, auth_url: data.body.auth_url }));
}

export function getReminders({ domain }: { domain: string }) {
  return axios(api(domain, 'reminders')).then(({ data }) => data.body);
}

export function getSettings({ domain }: { domain: string }) {
  return axios(api(domain, 'settings')).then(({ data }) => data.body);
}

export function getOffset({ domain }: { domain: string }) {
  return axios(api(domain, 'offset'), {
    params: {
      client_datetime: formatISOUtc(),
    },
  }).then(({ data }) => data.body);
}

export function getTestimonials({ domain, qty = 20 }: { domain: string; qty: number }) {
  return axios(api(domain, 'testimonials'), { params: { qty } }).then(({ data }) => data.body);
}

export function getPapers({ domain }: { domain: string }) {
  return axios(api(domain, 'paper')).then(({ data }) => data.body.paper_type);
}

export function getDisciplines({ domain }: { domain: string }) {
  return axios(api(domain, 'discipline')).then(({ data }) => ({
    discipline_sub_grouping: data.body.discipline_sub_grouping,
    discipline_sub: data.body.discipline_sub,
  }));
}

export function deleteSession({ domain }: { domain: string }) {
  return axios(api(domain, 'userSession'), { method: 'DELETE' });
}

// export function getLatestOrders({ domain }) {
//   return axios(api(domain, 'latestOrders')).then(({ data }) => data.body);
// }
export function getLatestOrders({
  domain,
  disciplineId = null,
}: {
  domain: string;
  disciplineId: string | null;
}) {
  const params = {
    ...(disciplineId && { discipline: disciplineId }),
  };

  return axios(api(domain, 'latestOrders'), { params }).then(({ data }) => data.body);
}

export async function getDictionaries<T>({
  domain,
  dictionaries = [],
}: {
  domain: string;
  dictionaries: string[];
}): Promise<T> {
  const { data } = await axios(`${api(domain, 'dictionaries')}/${dictionaries.join(',')}`);
  return data.body;
}

export function getWriters({
  domain,
  writers = [],
  config,
}: {
  domain: string;
  writers: string[];
  config?: AxiosRequestConfig;
}): Promise<Record<IWriter['slug'], IWriter>> {
  return axios(`${api(domain, 'writer')}/${writers.join(',')}`, config).then(
    ({ data }) => data.body,
  );
}

export function getWriterDetails({
  domain,
  writerSlug,
}: {
  domain: string;
  writerSlug: string;
}): Promise<Record<IWriter['slug'], IWriter>> {
  return axios(`${api(domain, 'writer')}/${writerSlug}`)
    .then(props => {
      return props.data.body;
    })
    .catch(e => ({ error: e.message }));
}

export function getWriterTestimonials({
  domain,
  writerSlug,
  params = { offset: 0, limit: 3 },
}: {
  domain: string;
  writerSlug: string;
  params: AxiosRequestConfig['params'];
}): Promise<{ items: WriterTestimonial[]; items_qty: number }> {
  return axios(api(domain, 'writerTestimonials').replace('{slug}', writerSlug), { params }).then(
    ({ data }) => data.body,
  );
}

export async function getWriterRecentReview({
  domain,
  writerSlug,
}: {
  domain: string;
  writerSlug: string;
}): Promise<{ items: WriterTestimonial[]; items_qty: number }> {
  const { data } = await axios(api(domain, 'writerTestimonials').replace('{slug}', writerSlug), {
    params: {
      offset: 0,
      limit: 1,
      highlighted_only: true,
    },
  });
  return data.body;
}

export function getWriterPortfolioList({
  domain,
  writerSlug,
  params = {},
}: {
  domain: string;
  writerSlug: string;
  params: AxiosRequestConfig['params'];
}) {
  return axios(api(domain, 'writerPortfolioList').replace('{slug}', writerSlug), { params }).then(
    ({ data }) => data.body,
  );
}

export function getWriterPortfolioDetails({
  domain,
  writerSlug,
  portfolioID,
}: {
  domain: string;
  writerSlug: string;
  portfolioID: number;
}) {
  return axios(
    api(domain, 'writerPortfolioDetails')
      .replace('{slug}', writerSlug)
      .replace('{id}', `${portfolioID}`),
  ).then(({ data }) => data.body);
}

export function getTopWriters({
  domain,
  config: { params = {}, ...rest } = {},
}: {
  domain: string;
  config: AxiosRequestConfig;
}) {
  const defaultParams = {
    discipline: '0',
    reviews_qty: '50',
    sort: 'rating',
    online: 'any',
    lang: 'en',
  };
  return axios(api(domain, 'topWriters'), {
    params: { ...defaultParams, ...params },
    ...rest,
  }).then(({ data }) => data.body);
}

export function getUserCheckAuth(state: PipeState) {
  const { login, errors, domain = '', policy_agree } = state || {};

  return axios(api(domain, 'userCheck'), {
    params: {
      login,
    },
  }).then(({ data: { body } }) => ({
    ...state,
    ...body,
    errors: {
      ...errors,
      policy_required: !policy_agree && body.policy_agree_required,
      user_blocked: !body.login_ability && !body.register_ability,
      login_incorrect: !body.login_ability && body.register_ability,
    },
  }));
}

/**
 * @TODO rename to getUserCheckOrder
 * @param {State} state
 * @returns {Promise<State>}
 */
export function getUserCheck(state: PipeState) {
  const { login, user_role, errors, domain = '', policy_agree } = state || {};

  return axios(api(domain, 'userCheck'), {
    params: {
      login,
      user_role: user_role || undefined,
    },
  }).then(({ data: { body } }) => ({
    ...state,
    ...body,
    errors: {
      ...errors,
      policy_required: !policy_agree && body.policy_agree_required,
      user_blocked: !body.login_ability && !body.register_ability && body.user_role_matched,
      login_incorrect:
        !body.login_ability && !body.user_role_matched && user_role === USER_ROLE_CUSTOMER
          ? !body.register_ability
          : body.register_ability,
      auth_token_required_user_not_matched:
        !!user_role && !body.user_role_matched && !body.register_ability,
    },
  }));
}

/**
 * @param {Object} state
 * @param {string} recaptcha_site_key
 */
export function genRecaptchaToken(state: PipeState) {
  const { recaptcha_site_key, recaptcha_action, errors } = state || {};
  return apipe(
    (): Promise<ReCaptchaInstance> => load(recaptcha_site_key, { autoHideBadge: true }),
    (recaptcha: ReCaptchaInstance) => recaptcha.execute(recaptcha_action),
    (captcha_token: ReturnType<ReCaptchaInstance['execute']>) => ({
      ...state,
      captcha_token,
      errors: { ...errors, recaptcha_gen_error: !captcha_token },
    }),
  )();
}

/**
 * @param {string} recaptcha_action
 * @param {State} state
 * @returns {State}
 */
export function setRecaptchaAction(recaptcha_action: string) {
  return (state: PipeState) => ({ ...state, recaptcha_action });
}

/**
 * @param {State} state
 * @returns {Promise<State>}
 */
export function postSession(state: PipeState) {
  const {
    login,
    password,
    utc_offset_min,
    client_type = CLIENT_TYPE_BROWSER,
    captcha_token,
    stay_signed_in,
    policy_agree,
    marketing_agree,
    domain,
  } = state || {};

  return axios({
    url: api(domain, 'userSession'),
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded;',
    },
    data: toUrlEncoded({
      login,
      password,
      utc_offset_min,
      client_type,
      captcha_token,
      stay_signed_in,
      policy_agree,
      marketing_agree,
    }),
  })
    .then(({ data: { body } }) => ({ ...state, ...body }))
    .catch(addApiError(state));
}

/**
 * @param {State} state
 * @returns {Promise<State>}
 */
export function getSession(state: PipeState) {
  const { errors = {}, domain } = state || {};
  return axios(api(domain, 'userSession'))
    .then(({ data = { body: undefined } }) =>
      data.body
        ? { ...state, ...data.body }
        : { ...state, errors: { ...errors, password_incorrect: true } },
    )
    .catch(addApiError(state));
}

/**
 * @param {State} state
 * @returns {Promise<State>}
 */
export function getCustomer(state: PipeState) {
  const { domain } = state || {};

  return axios(api(domain, 'customer')).then(({ data }) => ({ ...state, ...data.body }));
}

/**
 * @param {State} state
 * @returns {Promise<State>}
 */
export function postCustomer(state: PipeState) {
  const {
    login,
    utc_offset_min,
    force_auth = true,
    client_type = CLIENT_TYPE_BROWSER,
    policy_agree,
    marketing_agree,
    errors,
    domain,
  } = state || {};

  return policy_agree
    ? axios({
        url: api(domain, 'customer'),
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded;',
        },
        data: toUrlEncoded({
          login,
          utc_offset_min,
          force_auth,
          policy_agree,
          marketing_agree,
          client_type,
        }),
      })
        .then(() => ({
          ...state,
          login_ability: true,
          is_new_customer: true,
          password: '',
          errors: { ...errors, policy_required: undefined },
        }))
        .catch(e => {
          if (e.response) {
            const {
              data: {
                meta: {
                  response: { message_slug },
                },
              },
            } = e.response;

            return {
              ...state,
              errors: {
                ...errors,
                [message_slug]: true,
                policy_required: undefined,
              },
            };
          }
        })
    : {
        ...state,
        login_ability: false,
        errors: { ...errors, policy_required: true },
      };
}

/**
 * @deprecated
 * @param {State} state
 * @returns {Promise<State>}
 */
export function postPreOrder(state: PipeState) {
  const {
    paper_type,
    writer_req_slug,
    pages_qty,
    words_qty,
    deadline_datetime,
    captcha_token,
    utc_offset_min,
    domain,
    title,
  } = state || {};

  const deadlineUTC = addMinutes(deadline_datetime, -utc_offset_min);

  return axios({
    url: api(domain, 'preOrder'),
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded;',
    },
    data: toUrlEncoded({
      deadline_datetime: formatISOUtc(deadlineUTC),
      paper_type,
      pages_qty,
      words_qty,
      'paper-size-type': state['paper-size-type'],
      captcha_token,
      ...(writer_req_slug ? { writer_req_slug } : null),
      ...(title ? { title } : null),
    }),
  })
    .then(({ data: { body } }) => ({ ...state, ...body }))
    .catch(addApiError(state));
}

export function postDataBox(state: PipeState) {
  const TIME_TO_LIVE = 1800; // 30 minutes

  const {
    paper_type,
    title,
    discipline_sub,
    writer_req_slug,
    pages_qty,
    words_qty,
    deadline_datetime,
    domain,
    isAssignment,
    marketing_agree,
    policy_agree,
    description,
    files,
    data_type,
  } = state || {};
  const writingData = {
    paper_type,
    title,
    discipline_sub,
    writer_req_slug: writer_req_slug || undefined,
    description,
    pages_qty,
    words_qty,
    ['paper-size-type']: state['paper-size-type'],
    deadline_datetime: new Date(deadline_datetime).getTime(),
    create_datetime: new Date().getTime(),
    marketing_agree,
    policy_agree,
    files,
    ...(data_type ? { data_type } : undefined),
  };
  const assignmentData = {
    discipline_sub,
    discipline_txt: state.discipline_txt || '',
    writer_req_slug: writer_req_slug || undefined,
    create_datetime: new Date().getTime(),
    marketing_agree,
    policy_agree,
  };

  return axios({
    url: api(domain, 'databox'),
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    data: toUrlEncoded({
      data: JSON.stringify(isAssignment ? assignmentData : writingData),
      ttl: TIME_TO_LIVE,
    }),
  }).then(
    ({
      data: {
        body: { key },
      },
    }) => ({ ...state, data_box_key: key }),
  );
}

export function getDataBox(state: PipeState) {
  const { domain, data_box_key } = state || {};

  return axios(`${api(domain, 'databox')}/${data_box_key}`)
    .then(({ data }) => {
      let databox;

      const { body: { data: box = '' } = {} } = data;

      try {
        databox = JSON.parse(box);
      } catch (e) {
        databox = {};
      }

      return {
        ...state,
        databox,
      };
    })
    .catch(() => ({ ...state, databox: {} }));
}

/**
 * XXX side-effect window
 * @param {State} state
 * @returns {State}
 */
export function navigate(state: PipeState) {
  const { route, data_box_key } = state || {};
  window.location.href = `/api/en/1/users/redirect-to-dashboard${toSearchParams({
    route,
    data_box_key,
  })}`;
  return state;
}

/**
 * XXX side-effect window
 * @param {State} state
 * @returns {State}
 */
export function navigateSat(state: PipeState) {
  const { domain, data_box_key } = state || {};

  window.location.href = `https://${domain}/${toSearchParams({ data_box_key })}`;
  return state;
}

export const resetForm = (state: PipeState) => {
  const { login, policy_agree, captcha_token, domain } = state || {};

  return axios({
    url: api(domain, 'passwordRestore'),
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    data: toUrlEncoded({
      login,
      captcha_token,
      policy_agree,
    }),
  })
    .then(() => ({ ...state, passwordResetSuccess: true }))
    .catch(addApiError(state));
};

export const setNewPassword = (state: PipeState) => {
  const { code, password, domain } = state || {};

  return axios({
    url: api(domain, 'passwordRestore'),
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    data: toUrlEncoded({
      code,
      password,
    }),
  })
    .then(() => ({ ...state, passwordChanged: true }))

    .catch(addApiError(state));
};

export function navigateOAuth2(state: PipeState) {
  const { auth_url } = state || {};

  window.location.href = auth_url;
  return state;
}

export const validateCode = (state: PipeState) => {
  const { code, domain } = state || {};

  return axios({
    url: api(domain, 'validateCode'),
    method: 'GET',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    params: {
      code,
    },
  })
    .then(() => ({ ...state, codeIsValid: true }))
    .catch(addApiError(state));
};

/**
 * @param {State} state
 * @returns {Promise<State>}
 */
export function writerRegistration(state: PipeState) {
  const { domain, email, policy_agree, wreg, captcha_token, user_role } = state || {};

  return axios({
    url: api(domain, 'writerRegistration'),
    method: 'POST',
    data: toUrlEncoded({
      email,
      policy_agree,
      captcha_token,
      ...(wreg && { wreg }),
      ...(user_role && { user_role }),
    }),
  })
    .then(() => ({ ...state, applicationSuccessful: true }))
    .catch(addApiError(state));
}
/**
 * @param {State} state
 * @returns {Promise<State>}
 */
export function becomeWriterRequest(state: PipeState) {
  const { domain, name, email } = state || {};

  return axios({
    url: api(domain, 'becomeWriterRequest'),
    method: 'POST',
    data: toUrlEncoded({
      email,
      name,
    }),
  })
    .then(() => ({ ...state, applicationSuccessful: true }))
    .catch(addApiError(state));
}

/**
 * Get request for site stats
 * @param {{domain: string}} state
 * @returns {Promise<State>}
 */
export const getSiteStats = ({ domain }: { domain: string }) => {
  return axios(api(domain, 'statistics')).then(({ data }) => data.body);
};

/**
 *
 * @param {{pathToConfig: string}} pathToConfig path to static folder where JSON file so stored
 * @returns {Promise<State>}
 */
export const getMockData = ({ pathToMockConfig }: { pathToMockConfig: string }) => {
  if (pathToMockConfig) {
    return axios(pathToMockConfig)
      .then(({ data }) => ({ mock: data }))
      .catch(error => console.error(error));
  }
  return {
    mock: [],
  };
};

export const getDashboardConfig = () =>
  axios('/resources/dashboard/config.json')
    .then(({ data }) => data)
    .catch(error => console.error(error));

export const getOrderDetails = ({ domain, id }: { domain: string; id: string }) =>
  axios(api(domain, 'orderDetails').replace('{id}', id)).then(({ data }) => data.body);

export const getPostOrderDetails = ({ domain, hash }: { domain: string; hash: string }) =>
  axios(`${api(domain, 'postOrder')}/${hash}`).then(({ data }) => data.body);

/**
 * @param {State} state
 * @returns {Promise<State>}
 */
export const postPostOrderExperienceRating = (state: PipeState) => {
  const { domain, hash, values } = state || {};

  return axios({
    url: `${api(domain, 'postOrder')}/${hash}`,
    method: 'POST',
    data: toUrlEncoded({
      ...values,
      ...(values.grade && { grade: values.grade.value }),
    }),
  })
    .then(() => ({ ...state, success: true }))
    .catch(handleApiErrorsSlugs(state));
};

export const getPreAddedFiles = ({ domain }: { domain: string }): Promise<PreAddedFiles> => {
  return axios(api(domain, 'preAddFile')).then(({ data }) => data.body);
};

export const postPreAddFile = ({
  domain,
  file,
}: {
  domain: string;
  file: File;
}): Promise<{ orphanage_file: string }> => {
  const formData = new FormData();
  formData.append('file', file);

  return axios({
    url: `${api(domain, 'preAddFile')}`,
    method: 'POST',
    data: formData,
  }).then(res => res.data.body);
};
